"use client";

import ProductDetailsModal from "@/components/common/product-details-modal/product-details-modal";
import ProductIdeaModal from "@/components/common/product-idea";
import GrowWithUsModal from "@/modules/grow-with-us/components/grow-with-us-modal";
import { createContext, useContext, useEffect, useState } from "react";

import AuthModal from "@/components/new/common/auth-modal";
import UpdateProfileModal from "@/modules/update-profile/components/update-profile-modal";
import { useQuery } from "@tanstack/react-query";
import { usePathname } from "next/navigation";
import CreatePostModal from "@/modules/community/post/components/create-post/create-post-modal";
import ContactUsModal from "@/modules/contact-us/contact-us-modal";
import { ModalType } from "../types/modal-type";
import { TGetAccountUpgradeInfoResponseDTO } from "@/types/dto/account";
import { useAuth } from "./auth-provider";
import { BASE_URL } from "@/lib/constants";
import { getProductV2Info } from "@/services/productV2/productV2";
import { TProduct } from "@/types/productv2";

type TCreatePostModalContext = {
	openCreatePostModal: () => void;
	closeCreatePostModal: () => void;
};

interface IModalPayload {
	productSlug?: string;
	onAuthSuccess?: () => void;
	growWithUsMessage?: string;
	disableURLChange?: boolean;
}

interface ModalContextProps {
	productSlug: string;
	openModal: (type: ModalType, payload?: IModalPayload) => void;
	closeModal: (type: ModalType, payload?: IModalPayload) => void;
}

const ModalContext = createContext<ModalContextProps | undefined>(undefined);

const fetchUserUpgradeInfo = async () => {
	try {
		const response = await fetch(`${BASE_URL}/api/account/upgrade`);
		if (!response.ok) {
			return {
				isUpgradedToVIP: false,
				upgradedAt: null,
			};
		}

		const { data } = await response.json();

		return data;
	} catch (error) {
		console.log(error);

		return {
			isUpgradedToVIP: false,
			upgradedAt: null,
		};
	}
};

const getProductDetails = async (productSlug: string): Promise<TProduct> => {
	return await getProductV2Info(productSlug);
};

export const ModalProvider = ({ children }: { children: React.ReactNode }) => {
	const pathname = usePathname();

	const [productSlug, setProductSlug] = useState<string>("");
	const [isProductDetailsModalOpen, setIsProductDetailsModalOpen] = useState<boolean>(false);
	const [isAuthModalOpen, setIsAuthModalOpen] = useState<boolean>(false);
	const [isProductIdeaModalOpen, setIsProductIdeaModalOpen] = useState<boolean>(false);
	const [isGrowWithUsModalOpen, setIsGrowWithUsModalOpen] = useState<boolean>(false);
	const [isContactUsModalOpen, setIsContactUsModalOpen] = useState<boolean>(false);
	const [isUpdateProfileModalOpen, setIsUpdateProfileModalOpen] = useState<boolean>(false);
	const [growWithUsMessage, setGrowWithUsMessage] = useState<string>("");
	const [authSuccessCallback, setAuthSuccessCallback] = useState<(() => void) | undefined>();
	const [isVIPUpgradeModalOpen, setIsVIPUpgradeModalOpen] = useState(false);

	const { isLoggedIn } = useAuth();

	const { data: userUpgradeInfo } = useQuery<TGetAccountUpgradeInfoResponseDTO>({
		queryKey: ["userUpgradeInfo"],
		queryFn: fetchUserUpgradeInfo,
		enabled: !!isLoggedIn,
		refetchOnWindowFocus: false,
	});

	const { data: productDetails } = useQuery<TProduct>({
		queryKey: ["productDetails", productSlug],
		queryFn: () => getProductDetails(productSlug),
		enabled: !!isProductDetailsModalOpen && !!productSlug,
		refetchOnWindowFocus: false,
	});

	const openModal = (type: ModalType, payload?: IModalPayload) => {
		switch (type) {
			case ModalType.ProductDetails:
				setIsProductDetailsModalOpen(true);
				if (payload?.productSlug) {
					setProductSlug(payload.productSlug);
				}
				break;
			case ModalType.Auth:
				if (payload?.onAuthSuccess) {
					setAuthSuccessCallback(() => payload.onAuthSuccess);
				}
				setIsAuthModalOpen(true);
				break;
			case ModalType.ProductIdea:
				setIsProductIdeaModalOpen(true);
				break;
			case ModalType.GrowWithUs:
				if (payload?.growWithUsMessage) {
					setGrowWithUsMessage(payload.growWithUsMessage);
				}
				setIsGrowWithUsModalOpen(true);
				break;
			case ModalType.ContactUs:
				setIsContactUsModalOpen(true);
				break;
			case ModalType.UpdateProfile:
				setIsUpdateProfileModalOpen(true);
				break;
			case ModalType.VIP:
				setIsVIPUpgradeModalOpen(true);
				break;
			default:
				break;
		}
	};

	const closeModal = (type: ModalType, payload?: IModalPayload) => {
		switch (type) {
			case ModalType.ProductDetails:
				if (!payload?.disableURLChange) {
					history.pushState({}, "", pathname);
				}
				setIsProductDetailsModalOpen(false);
				break;
			case ModalType.Auth:
				setIsAuthModalOpen(false);
				setAuthSuccessCallback(undefined);
				break;
			case ModalType.ProductIdea:
				setIsProductIdeaModalOpen(false);
				break;
			case ModalType.GrowWithUs:
				setIsGrowWithUsModalOpen(false);
				break;
			case ModalType.ContactUs:
				setIsContactUsModalOpen(false);
				break;
			case ModalType.UpdateProfile:
				setIsUpdateProfileModalOpen(false);
				break;
			default:
				break;
		}
	};

	useEffect(() => {
		console.log(isVIPUpgradeModalOpen);

		if (userUpgradeInfo?.isUpgradedToVIP) {
			openModal(ModalType.VIP);
		}
	}, [userUpgradeInfo]);

	return (
		<ModalContext.Provider
			value={{
				productSlug,
				openModal,
				closeModal,
			}}
		>
			{children}

			<AuthModal
				isOpen={isAuthModalOpen}
				onClose={() => closeModal(ModalType.Auth)}
				onAuthSuccess={authSuccessCallback}
			/>

			<ProductDetailsModal
				isOpen={isProductDetailsModalOpen}
				onClose={() => closeModal(ModalType.ProductDetails)}
				slug={productSlug}
				product={productDetails as TProduct}
			/>

			<ProductIdeaModal
				isOpen={isProductIdeaModalOpen}
				onClose={() => closeModal(ModalType.ProductIdea)}
			/>

			<GrowWithUsModal
				isOpen={isGrowWithUsModalOpen}
				onClose={() => closeModal(ModalType.GrowWithUs)}
				message={growWithUsMessage}
			/>

			<ContactUsModal
				isOpen={isContactUsModalOpen}
				onClose={() => closeModal(ModalType.ContactUs)}
			/>

			<UpdateProfileModal
				isOpen={isUpdateProfileModalOpen}
				onClose={() => closeModal(ModalType.UpdateProfile)}
			/>
		</ModalContext.Provider>
	);
};

export const useModal = () => {
	const context = useContext(ModalContext);
	if (context === undefined) {
		throw new Error("useModal must be used within a ModalProvider");
	}
	return context;
};

const CreatePostModalContext = createContext<TCreatePostModalContext | undefined>(undefined);

export const CreatePostModalProvider = ({ children }: { children: React.ReactNode }) => {
	const [isCreatePostModalOpen, setIsCreatePostModalOpen] = useState(false);

	const openCreatePostModal = () => setIsCreatePostModalOpen(true);
	const closeCreatePostModal = () => setIsCreatePostModalOpen(false);

	return (
		<CreatePostModalContext.Provider value={{ openCreatePostModal, closeCreatePostModal }}>
			{children}
			<CreatePostModal open={isCreatePostModalOpen} setOpen={setIsCreatePostModalOpen} />
		</CreatePostModalContext.Provider>
	);
};

export const useCreatePostModal = () => {
	const context = useContext(CreatePostModalContext);
	if (context === undefined) {
		throw new Error("useCreatePostModal must be used within a CreatePostModalProvider");
	}
	return context;
};
